/**
 * Expense Splitting Utilities
 * 
 * Functions for calculating expense splits and settlements
 */

import type { Expense, ExpenseSplit, SplitType } from '@/services/expenses';

/** Stable key for comparing split configs (avoids parent/child feedback loops). */
export function expenseSplitSnapshotKey(split: ExpenseSplit | null | undefined): string {
  if (!split || split.splitType === 'none') return '__none__';
  const sortedAmounts = split.splitAmounts
    ? Object.keys(split.splitAmounts)
        .sort()
        .reduce<Record<string, number>>((acc, key) => {
          const value = split.splitAmounts![key];
          acc[key] = Math.round(value * 100) / 100;
          return acc;
        }, {})
    : undefined;
  return JSON.stringify({
    t: split.splitType,
    a: [...split.splitAmong].sort(),
    p: split.paidBy,
    m: sortedAmounts,
  });
}

/**
 * Calculate the amount each person owes for an expense
 */
export function calculateSplitAmounts(
  expense: Expense,
  split: ExpenseSplit
): Record<string, number> {
  const { amount, currency } = expense;
  const { splitType, splitAmong, splitAmounts } = split;

  if (splitType === 'none' || splitAmong.length === 0) {
    return {};
  }

  if (splitType === 'equal') {
    const perPerson = amount / splitAmong.length;
    const result: Record<string, number> = {};
    splitAmong.forEach((userId) => {
      result[userId] = perPerson;
    });
    return result;
  }

  if (splitType === 'custom' && splitAmounts) {
    // Validate that custom amounts sum to total
    const total = Object.values(splitAmounts).reduce((sum, amt) => sum + amt, 0);
    if (Math.abs(total - amount) > 0.01) {
      // Allow small rounding differences
      console.warn(`[ExpenseSplitting] Custom split amounts (${total}) don't match expense amount (${amount})`);
    }
    return { ...splitAmounts };
  }

  return {};
}

/**
 * User's portion of the bill (their share). Pair with paid amount: paid − share = net owed to/from others.
 */
export function getUserShare(
  expense: Expense,
  userId: string
): number {
  if (!expense.split || expense.split.splitType === 'none') {
    return 0;
  }

  const splitAmounts = calculateSplitAmounts(expense, expense.split);
  return splitAmounts[userId] || 0;
}

/**
 * Full expense amount if this user paid; otherwise 0.
 */
export function getUserPaid(expense: Expense, userId: string): number {
  if (!expense.split || expense.split.paidBy !== userId) {
    return 0;
  }
  return expense.amount;
}

/**
 * Calculate net balance for a user across all expenses
 * Positive = user is owed money
 * Negative = user owes money
 */
export function calculateUserBalance(
  expenses: Expense[],
  userId: string
): number {
  let balance = 0;

  expenses.forEach((expense) => {
    if (!expense.split || expense.split.splitType === 'none') {
      return;
    }

    const paid = getUserPaid(expense, userId);
    const owes = getUserShare(expense, userId);

    balance += paid - owes;
  });

  return balance;
}

/**
 * Settlement between two users
 */
export type Settlement = {
  from: string; // userId who owes
  to: string; // userId who is owed
  amount: number;
  currency: string;
};

/**
 * Collect all participant ids referenced by split expenses.
 * This keeps historical settlement math intact when trip membership changes.
 */
export function collectExpenseParticipantIds(expenses: Expense[]): string[] {
  const participantIds = new Set<string>();

  for (const expense of expenses) {
    const split = expense.split;
    if (!split || split.splitType === 'none') continue;

    if (typeof split.paidBy === 'string' && split.paidBy.trim()) {
      participantIds.add(split.paidBy);
    }

    for (const userId of split.splitAmong ?? []) {
      if (typeof userId === 'string' && userId.trim()) {
        participantIds.add(userId);
      }
    }

    if (split.splitAmounts) {
      for (const userId of Object.keys(split.splitAmounts)) {
        if (typeof userId === 'string' && userId.trim()) {
          participantIds.add(userId);
        }
      }
    }
  }

  return Array.from(participantIds);
}

/**
 * Calculate all settlements needed to balance expenses
 * Uses a simplified algorithm that minimizes transactions
 */
export function calculateSettlements(
  expenses: Expense[],
  userIds: string[]
): Settlement[] {
  // Calculate balance for each user
  const balances: Record<string, number> = {};
  userIds.forEach((userId) => {
    balances[userId] = calculateUserBalance(expenses, userId);
  });

  // Get currency from first expense (assuming all same currency for now)
  const currency = expenses.length > 0 ? expenses[0].currency : 'USD';

  // Separate users who owe money (negative balance) and are owed money (positive balance)
  const debtors: Array<{ userId: string; amount: number }> = [];
  const creditors: Array<{ userId: string; amount: number }> = [];

  Object.entries(balances).forEach(([userId, balance]) => {
    if (balance < -0.01) {
      // Owing money (negative)
      debtors.push({ userId, amount: Math.abs(balance) });
    } else if (balance > 0.01) {
      // Owed money (positive)
      creditors.push({ userId, amount: balance });
    }
  });

  // Sort by amount (largest first)
  debtors.sort((a, b) => b.amount - a.amount);
  creditors.sort((a, b) => b.amount - a.amount);

  const settlements: Settlement[] = [];
  let debtorIndex = 0;
  let creditorIndex = 0;

  // Match debtors with creditors to minimize transactions
  while (debtorIndex < debtors.length && creditorIndex < creditors.length) {
    const debtor = debtors[debtorIndex];
    const creditor = creditors[creditorIndex];

    const settlementAmount = Math.min(debtor.amount, creditor.amount);

    settlements.push({
      from: debtor.userId,
      to: creditor.userId,
      amount: settlementAmount,
      currency,
    });

    debtor.amount -= settlementAmount;
    creditor.amount -= settlementAmount;

    if (debtor.amount < 0.01) {
      debtorIndex++;
    }
    if (creditor.amount < 0.01) {
      creditorIndex++;
    }
  }

  return settlements;
}

/**
 * Get summary of expense splitting for a trip
 */
export type ExpenseSplitSummary = {
  totalExpenses: number;
  splitExpenses: number;
  totalSplitAmount: number;
  currency: string;
  userBalances: Record<string, number>;
  settlements: Settlement[];
};

export function calculateExpenseSplitSummary(
  expenses: Expense[],
  userIds: string[]
): ExpenseSplitSummary {
  const splitExpenses = expenses.filter((e) => e.split && e.split.splitType !== 'none');
  const currency = expenses.length > 0 ? expenses[0].currency : 'USD';

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const totalSplitAmount = splitExpenses.reduce((sum, e) => sum + e.amount, 0);

  const userBalances: Record<string, number> = {};
  userIds.forEach((userId) => {
    userBalances[userId] = calculateUserBalance(expenses, userId);
  });

  const settlements = calculateSettlements(expenses, userIds);

  return {
    totalExpenses,
    splitExpenses: splitExpenses.length,
    totalSplitAmount,
    currency,
    userBalances,
    settlements,
  };
}

/**
 * Validate expense split configuration
 */
export function validateExpenseSplit(
  expense: Expense,
  split: ExpenseSplit
): { valid: boolean; error?: string } {
  if (split.splitType === 'none') {
    return { valid: true };
  }

  if (split.splitAmong.length === 0) {
    return { valid: false, error: 'Must select at least one person to split with' };
  }

  if (!split.paidBy) {
    return { valid: false, error: 'Must specify who paid for this expense' };
  }

  if (split.splitType === 'equal') {
    if (split.splitAmong.length === 0) {
      return { valid: false, error: 'Must select at least one person to split with' };
    }
    return { valid: true };
  }

  if (split.splitType === 'custom') {
    if (!split.splitAmounts) {
      return { valid: false, error: 'Must specify custom split amounts' };
    }

    const total = Object.values(split.splitAmounts).reduce((sum, amt) => sum + amt, 0);
    const difference = Math.abs(total - expense.amount);

    if (difference > 0.01) {
      return {
        valid: false,
        error: `Custom split amounts (${total.toFixed(2)}) must equal expense amount (${expense.amount.toFixed(2)})`,
      };
    }

    // Check that all split amounts are positive
    const negativeAmounts = Object.values(split.splitAmounts).filter((amt) => amt <= 0);
    if (negativeAmounts.length > 0) {
      return { valid: false, error: 'All split amounts must be greater than 0' };
    }

    // Check that all split amounts correspond to splitAmong users
    const splitUserIds = new Set(split.splitAmong);
    const amountUserIds = new Set(Object.keys(split.splitAmounts));
    
    if (splitUserIds.size !== amountUserIds.size) {
      return { valid: false, error: 'Split amounts must be specified for all selected users' };
    }

    for (const userId of splitUserIds) {
      if (!amountUserIds.has(userId)) {
        return { valid: false, error: `Missing split amount for user ${userId}` };
      }
    }
  }

  return { valid: true };
}


